/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */
import { useEffect, useState, ChangeEvent } from 'react';
import { connect } from 'react-redux';
import get from 'lodash/get';
import { HeroImageSectionFront, useFlags, SecurityHelper } from 'sarsaparilla';
import LoadingWrapper from '../components/LoadingWrapper';
import OpenLotteries from '../components/OpenLotteries';
import HowTheLotteriesWorkAccordion from '../components/HowTheLotteriesWorkAccordion';
import AboutLotteries from '../components/AboutLotteries';
import LotteryStatistics from '../components/LotteryStatistics';
import UpcomingLotteries from '../components/UpcomingLotteries';
import GovernmentShutdownNotification from '../components/GovernmentShutdownNotification';

import fetchLotteries from '../actions/lottery';
import fetchLotteryStatistics from '../actions/lotteryStatistics';
import { Lottery } from '../types/lottery';

interface AvailableLotteriesPageProps {
    openLotteries?: Lottery[];
    futureLotteries?: Lottery[];
    loading?: boolean;
    dispatch?: any;
    lotteryStatistics?: any[];
    lotteryStatisticsLoading?: boolean;
}
export function AvailableLotteriesPage({
    loading = false,
    openLotteries,
    futureLotteries,
    dispatch,
    lotteryStatistics,
    lotteryStatisticsLoading,
}: AvailableLotteriesPageProps) {
    const [selectVal, setSelectVal] = useState(new Date().getFullYear().toString());
    const [globalFilterState, setGlobalFilterState] = useState('');

    const { enableLotteryTransparency, lotteryListExcludeLocations } = useFlags();

    useEffect(() => {
        dispatch(fetchLotteries(lotteryListExcludeLocations));
    }, [lotteryListExcludeLocations]);

    useEffect(() => {
        dispatch(fetchLotteryStatistics(selectVal));
    }, [selectVal]);

    const enterALotteryText = `There are many incredible sites, events and activities available through ${process.env.SITE_NAME}, and,
    given the high volume of interest, some opportunities require a lottery process to limit traffic, enhance your
    experience and reduce our footprint. Our lottery process is based on a fair and randomized distribution system to
    manage access and ensure equitable participation.`;

    const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
        setSelectVal(e.target.value);
    };

    const header = SecurityHelper.isMobileApp() ? null : (
        <HeroImageSectionFront
            title={
                enableLotteryTransparency
                    ? 'Lottery Statistics and Information'
                    : 'Enter a Lottery'
            }
            text={enableLotteryTransparency ? undefined : enterALotteryText}
            backgroundImagePath="/img/lottery/hero/lottery-hero"
            backgroundImageAltText="A lake surrounded by tall mountains"
            backgroundImageColorOverlay="#19467b"
            backgroundImageColorOverlayOpacity={0.3}
        />
    );

    const lotteries = SecurityHelper.isMobileApp() ? null : (
        <>
            <OpenLotteries lotteries={openLotteries} />

            <div className="lottery-full-width-bg mb-0">
                <UpcomingLotteries lotteries={futureLotteries} />
            </div>
        </>
    );

    return (
        <main>
            {header}

            <LoadingWrapper loading={loading} size="lg">
                <GovernmentShutdownNotification />

                {enableLotteryTransparency && (
                    <>
                        <AboutLotteries />

                        <LotteryStatistics
                            lotteryStatistics={lotteryStatistics}
                            selectVal={selectVal}
                            globalFilterState={globalFilterState}
                            setGlobalFilterState={setGlobalFilterState}
                            handleSelectChange={handleSelectChange}
                            lotteryStatisticsLoading={lotteryStatisticsLoading}
                        />
                        <HowTheLotteriesWorkAccordion />
                    </>
                )}

                {lotteries}
            </LoadingWrapper>
        </main>
    );
}

const mapStateToProps = (state: any) => ({
    openLotteries: get(state, 'lottery.open_lotteries', []),
    futureLotteries: get(state, 'lottery.future_lotteries', []),
    loading: get(state, 'lottery.lotteries_loading', false),
    lotteryStatistics: get(state, 'lottery.lotteryStatistics', []),
    lotteryStatisticsLoading: get(state, 'lottery.lotteryStatisticsLoading', false),
});

export default connect(mapStateToProps)(AvailableLotteriesPage);
